import cx from "classnames";
import {
  Children,
  ComponentPropsWithoutRef,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./SideNav.module.scss";
import ArrowDown from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_down.svg";
import { Icon } from "../Icon";
import { sideNavKeyboardNavigation } from "./internal/utils";
import { useSideNav } from "./useSideNav";

/**
 * Props for the SideNavCollapsible component
 * @extends Omit<ComponentPropsWithoutRef<"li">, "onClick">
 */
export type SideNavCollapsibleProps = Omit<
  ComponentPropsWithoutRef<"li">,
  "onClick"
> & {
  /**
   * The label text displayed for the collapsible section
   */
  label: string;
  /**
   * Function called when the collapsible button is clicked
   */
  onClick?: ComponentPropsWithoutRef<"button">["onClick"];
  /**
   * Function called when a key is pressed on the collapsible button
   */
  onKeyDown?: ComponentPropsWithoutRef<"button">["onKeyDown"];
  /**
   * Initial expansion state for uncontrolled usage
   * @default false
   */
  defaultExpanded?: boolean;
  /**
   * Controlled expansion state
   */
  expanded?: boolean;
};

/**
 * SideNavCollapsible component for expandable navigation sections within a SideNav.
 *
 * Features:
 * - Expandable/collapsible navigation sections
 * - Controlled and uncontrolled expansion states
 * - Keyboard navigation with arrow keys for expansion
 * - Automatic expansion when child links are active
 * - Accessible with proper ARIA expanded states
 * - Visual indicators for active children
 * - Integration with SideNav keyboard navigation
 *
 * @example
 * <SideNav.Collapsible label="Components">
 *   <SideNav.Link id="button" href="/components/button">
 *     Button
 *   </SideNav.Link>
 *   <SideNav.Link id="input" href="/components/input">
 *     Input
 *   </SideNav.Link>
 * </SideNav.Collapsible>
 */
export const SideNavCollapsible = forwardRef<
  HTMLLIElement,
  SideNavCollapsibleProps
>((props, ref) => {
  const {
    className,
    children,
    label,
    onClick,
    onKeyDown,
    defaultExpanded = false,
    expanded: expandedControlled,
    ...rest
  } = props;
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const [hasSelected, setHasSelected] = useState<boolean>(false);
  const sideNavGroupLabelRef = useRef<HTMLButtonElement>(null);
  const groupRef = useRef<HTMLUListElement>(null);
  const sideNavGroupClassNames = cx(styles["sidenav-collapsible"], className);
  const { links, reCalculateLinks } = useSideNav();
  const groupId = useId();

  const setExpandedUncontrolled = (newState?: boolean) => {
    if (expandedControlled === undefined)
      setExpanded((prev) => {
        if (newState !== undefined) return newState;
        return !prev;
      });
  };

  useEffect(() => {
    if (expandedControlled !== undefined) {
      setExpanded(expandedControlled);
    }
  }, [expandedControlled]);
  const { activeIds } = useSideNav();

  const childIds: string[] = useMemo(
    () =>
      Children.map(children, (child) => (child as ReactElement).props)?.map(
        (item) => item.id,
      ) || [],
    [children],
  );

  function handleOnClick(e: MouseEvent<HTMLButtonElement>) {
    setExpandedUncontrolled();
    setTimeout(() => reCalculateLinks(), 0);

    // find current item that has tabindex 0
    const current = Array.prototype.slice
      .call(links)
      .filter((child) => (child as HTMLElement).tabIndex === 0)[0];

    // remove above from tabbable
    if (current) current.setAttribute("tabindex", "-1");

    // make label button tabbable
    sideNavGroupLabelRef.current?.setAttribute("tabindex", "0");
    sideNavGroupLabelRef.current?.focus();
    onClick?.(e);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    sideNavKeyboardNavigation(e, links, sideNavGroupLabelRef);
    switch (e.key) {
      case "ArrowRight":
        setExpanded(true);
        setTimeout(() => reCalculateLinks(), 0);
        break;
      case "ArrowLeft":
        setExpanded(false);
        setTimeout(() => reCalculateLinks(), 0);
        break;

      default:
        break;
    }
    onKeyDown?.(e);
  }

  useLayoutEffect(() => {
    const hasActiveChildren = childIds.some((id) => activeIds.has(id));
    if (hasActiveChildren) {
      setExpandedUncontrolled(true);
      setHasSelected(true);
      setTimeout(() => {
        const selectedChild =
          groupRef.current?.querySelector(`[active='true']`);
        selectedChild?.setAttribute("tabindex", "0");
        reCalculateLinks();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeIds.size || !childIds.some((id) => activeIds.has(id))) {
      setHasSelected(false);
      return;
    }
    setHasSelected(true);
  }, [activeIds, childIds]);

  return (
    <li
      ref={ref}
      data-anv="sidenav-collapsible"
      {...rest}
      className={sideNavGroupClassNames}
    >
      <button
        aria-expanded={expanded}
        aria-controls={groupId}
        type="button"
        tabIndex={-1}
        onClick={handleOnClick}
        ref={sideNavGroupLabelRef}
        className={cx(styles["sidenav-link"], {
          [styles["has-indicator"]]: hasSelected && !expanded,
        })}
        onKeyDown={handleKeyDown}
      >
        {label}
        <Icon className={styles["arrow"]} inherit svg={ArrowDown} aria-hidden />
      </button>
      <ul
        id={groupId}
        ref={groupRef}
        className={cx(styles["group"], !expanded && styles.hidden)}
      >
        {children}
      </ul>
    </li>
  );
});

SideNavCollapsible.displayName = "SideNavCollapsible";
